/**
 * REQ-002 MARKETING / FASE 1 (BHM-CDX-DIR-098) — Marcas + Brand Brain.
 * Cliente API. El backend (RBAC + org scoping) es la autoridad real.
 */
import { api } from '../utils/api';
import type { HubMarketingBrandTone } from '@barmetvault/shared';

/**
 * Adaptación mínima del contrato HubMarketingBrandBrain (snake_case heredado del Next.js
 * legacy) a la convención camelCase del stack actual (Prisma/NestJS/TS). Additive: shared
 * no cambia. Los nombres de campo coinciden 1:1 con el contrato.
 */
export interface MarketingBrand {
  id: string;
  orgId: string;
  name: string;
  tone: HubMarketingBrandTone | string;
  description: string;
  targetAudience: string;
  keyMessages: string[];
  avoid: string[];
  hashtags: string[];
  competitors: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface MarketingBrandInput {
  name: string;
  tone?: string;
  description?: string;
  targetAudience?: string;
  keyMessages?: string[];
  avoid?: string[];
  hashtags?: string[];
  competitors?: string[];
}

export const marketingApi = {
  listBrands: async () => (await api.get<MarketingBrand[]>('/marketing/brands')).data,
  getBrand: async (id: string) => (await api.get<MarketingBrand>(`/marketing/brands/${id}`)).data,
  createBrand: async (input: MarketingBrandInput) => (await api.post<MarketingBrand>('/marketing/brands', input)).data,
  updateBrand: async (id: string, input: Partial<MarketingBrandInput>) =>
    (await api.patch<MarketingBrand>(`/marketing/brands/${id}`, input)).data,
  // ─── FASE 2 (BHM-CDX-DIR-099) — Posts ────────────────────────────────────
  listPosts: async (brandId?: string) =>
    (await api.get<MarketingPost[]>('/marketing/posts', { params: brandId ? { brandId } : {} })).data,
  getPost: async (id: string) => (await api.get<MarketingPost>(`/marketing/posts/${id}`)).data,
  createPost: async (input: MarketingPostInput) => (await api.post<MarketingPost>('/marketing/posts', input)).data,
  updatePost: async (id: string, input: Partial<MarketingPostInput>) =>
    (await api.patch<MarketingPost>(`/marketing/posts/${id}`, input)).data,
  submitPost: async (id: string) => (await api.post<MarketingPost>(`/marketing/posts/${id}/submit`)).data,
  // ─── FASE 3 (BHM-CDX-DIR-100) — Aprobaciones ─────────────────────────────
  approvePost: async (id: string, comment?: string) =>
    (await api.post<MarketingPost>(`/marketing/posts/${id}/approve`, { comment })).data,
  rejectPost: async (id: string, comment: string) =>
    (await api.post<MarketingPost>(`/marketing/posts/${id}/reject`, { comment })).data,
  listApprovals: async (postId: string) =>
    (await api.get<MarketingApproval[]>(`/marketing/posts/${postId}/approvals`)).data,
};

// ─── FASE 3 (BHM-CDX-DIR-100) — Historial de decisiones ────────────────────

export interface MarketingApproval {
  id: string;
  orgId: string;
  postId: string;
  actorId: string;
  decision: 'APPROVED' | 'REJECTED' | string;
  comment: string;
  createdAt?: string;
}

// ─── FASE 2 (BHM-CDX-DIR-099) — Posts ──────────────────────────────────────

/** HubMarketingPostStatus del contrato shared. */
export type MarketingPostStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'scheduled'
  | 'published'
  | 'failed';

/**
 * Adaptación camelCase del contrato HubMarketingPost (snake_case heredado), 1:1.
 * HubMarketingPostAnalytics NO se modela aquí: sin fuente real = NO_DATA (fase posterior).
 */
export interface MarketingPost {
  id: string;
  orgId: string;
  brandId: string;
  userId: string;
  content: string;
  platforms: string[];
  mediaUrls: string[];
  scheduledAt?: string | null;
  publishedAt?: string | null;
  status: MarketingPostStatus | string;
  approvalStatus?: string | null;
  approvedBy?: string | null;
  rejectionReason?: string | null;
  hashtags: string[];
  aiGenerated: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface MarketingPostInput {
  brandId?: string;
  content?: string;
  platforms?: string[];
  mediaUrls?: string[];
  hashtags?: string[];
}
